import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

import { AppBar, Box, styled, Toolbar } from '@mui/material';
import clsx from 'clsx';
import * as PATHS from 'constants/paths';
import { SUBSCRIPTION_PLAN, SUBSCRIPTION_PLAN_2 } from 'constants/paths';
import { useBreakpoints } from 'hooks';

import { ReactComponent as Logo } from 'assets/icons/Logo.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/PlusIcon.svg';

import { useAuthenticated } from 'hooks/useAuthenticated';

import { Button } from 'components/_basic/Button/Button';
import { useAuthModalsContext } from 'components/AuthModals';
import { LanguageSettings } from 'components/LanguageSettings/LanguageSettings';

import { useHeaderLogic } from './HeaderLogicProvider';
import { MobileBurgerMenu } from './MobileBurgerMenu';
import { AccountSettings } from '../AccountSettings/AccountSettings';

import styles from './Header.module.scss';

const Offset = styled('div')(({ theme }) => theme.mixins.toolbar);

export const Header: FC = () => {
  const { t } = useTranslation();
  const { isAboveMobile } = useBreakpoints();

  const { isAuthenticated, isAuthConfiguring } = useAuthenticated();
  const { setIsModalOpened } = useAuthModalsContext();

  const {
    isUserAuth: authenticated,
    isUpgradeButtonVisible,
    isPayedSubscription,
    isFeeTrialButtonVisible,
    handleClickFreeTrial,
    handleWorkspaceClick,
    handleClickCreateProject,
    handleClickUpgradePlan,
    handleSignInModalOpen,
    handleSignUpModalOpen,
  } = useHeaderLogic();

  const [searchParams] = useSearchParams();

  const projectBriefDescription = searchParams.get('projectBriefDescription');
  const companyName = searchParams.get('companyName');
  const address = searchParams.get('address');
  const companyBriefDescription = searchParams.get('companyBriefDescription');
  const login = searchParams.get('login');
  const pSignUp = searchParams.get('pSignUp'); // only payed subscription plans will be available
  const location = useLocation();
  const isSubscriptionPage = location.pathname === SUBSCRIPTION_PLAN;
  const isOnlyPayedSubscriptionPage = location.pathname === SUBSCRIPTION_PLAN_2;

  const handleSignInClick = () => {
    localStorage.removeItem('freePlanClicked');
    localStorage.removeItem('choosedSubscription');
    handleSignInModalOpen();
  };
  const handleSignUpClick = () => {
    localStorage.removeItem('freePlanClicked');
    localStorage.removeItem('choosedSubscription');
    handleSignUpModalOpen();
  };

  // const handleWorkspaceButtonClick = () => {
  //   handleWorkspaceClick();
  //   if (isPayedSubscription) {
  //   } else {
  //     return;
  //   }
  // };

  useEffect(() => {
    if (isAuthConfiguring) return;
    if (!isAuthenticated && !isSubscriptionPage && !isOnlyPayedSubscriptionPage && login) {
      handleSignInModalOpen();
    } else if ((!isAuthenticated && !isSubscriptionPage && !isOnlyPayedSubscriptionPage) || pSignUp) {
      handleSignUpModalOpen();
    } else {
      setIsModalOpened(false);
    }
  }, [isAuthenticated, isAuthConfiguring, login]);

  useEffect(() => {
    if (projectBriefDescription) {
      localStorage.setItem('projectBriefDescription', projectBriefDescription);
    }
    if (companyBriefDescription) {
      localStorage.setItem('companyBriefDescription', companyBriefDescription);
    }
    if (address) {
      localStorage.setItem('address', address);
    }
    if (companyName) {
      localStorage.setItem('companyName', companyName);
    }
    if (pSignUp) {
      localStorage.setItem('onlyPayedSignUp', pSignUp);
    }
  }, [companyBriefDescription, projectBriefDescription, address, companyName, pSignUp]);

  const linkForLogo = authenticated
    ? isPayedSubscription
      ? PATHS.WORKSPACE
      : isOnlyPayedSubscriptionPage
        ? PATHS.SUBSCRIPTION_PLAN_2
        : PATHS.SUBSCRIPTION_PLAN
    : PATHS.SUBSCRIPTION_PLAN;

  if (isAboveMobile) {
    return (
      <>
        <AppBar position="fixed" className={styles.container}>
          <Toolbar disableGutters sx={{ width: '100%' }}>
            <Link to={linkForLogo} style={{ display: 'flex' }}>
              <Logo className={styles['logo']} />
            </Link>
            <Box className={styles.headerCtaContainer}>
              <LanguageSettings />
              {authenticated && !localStorage.getItem('onlyPayedSignUp') && (
                <Button className={styles.workspaceButton} onClick={handleWorkspaceClick}>
                  {t('workspace')}
                </Button>
              )}
              {authenticated && isPayedSubscription && (
                <Button
                  size="small"
                  color="primary"
                  variant="outlined"
                  onClick={handleClickCreateProject}
                  endIcon={<PlusIcon style={{ fill: 'inherit' }} />}
                >
                  {t('createProject')}
                </Button>
              )}

              {isFeeTrialButtonVisible && !isSubscriptionPage && !isOnlyPayedSubscriptionPage && (
                <Button size="small" color="primary" variant="outlined" onClick={handleClickFreeTrial}>
                  {t('freeTrial')}
                </Button>
              )}

              {isUpgradeButtonVisible && (
                <Button size="small" color="secondary" variant="outlined" onClick={handleClickUpgradePlan}>
                  {t('upgradePlan')}
                </Button>
              )}
              {authenticated && (
                <Link
                  to={
                    'https://docs.google.com/forms/d/e/1FAIpQLScSMGWjzOH14JTdywOT37ED3IZu79WM3Y3qgofxReNgQi2sUQ/viewform'
                  }
                  target="_blank"
                >
                  <Button size="small" color="secondary" variant="outlined" onClick={() => null}>
                    {t('giveFeedback')}
                  </Button>
                </Link>
              )}
              {!authenticated ? (
                <Box className={styles.authButtonsGroup}>
                  <Button
                    size="small"
                    variant="text"
                    color="inherit"
                    className={clsx(styles.button, styles.loginButton)}
                    onClick={handleSignInClick}
                  >
                    {t('logIn')}
                  </Button>
                  <Button size="small" variant="contained" className={clsx(styles.button)} onClick={handleSignUpClick}>
                    {t('signUp')}
                  </Button>
                </Box>
              ) : (
                <AccountSettings />
              )}
            </Box>
          </Toolbar>
        </AppBar>
        <Offset />
      </>
    );
  }

  return (
    <>
      <AppBar position="fixed" className={styles.container}>
        <Toolbar disableGutters sx={{ width: '100%', justifyContent: 'space-between' }}>
          <Link to={authenticated ? PATHS.WORKSPACE : PATHS.HOME} style={{ display: 'flex' }}>
            <Logo className={styles['logo']} />
          </Link>
          <Box display={'flex'} gap={'16px'} alignItems={'center'}>
            <LanguageSettings />
            {isUpgradeButtonVisible && (
              <Button size="small" color="secondary" variant="outlined" onClick={handleClickUpgradePlan}>
                {t('upgradePlan')}
              </Button>
            )}
            <MobileBurgerMenu />
          </Box>
        </Toolbar>
      </AppBar>
      <Offset />
    </>
  );
};
